import { create } from 'zustand';
import { ProjectRelatedData, TextBlockData, TextData, TextTagData } from '../dto/EntityRepository';
import { TextBlockType } from '../enum/EnumRepository';


interface TextViewState {
  activeText: TextData | null;
  multiSelectedText: TextData[];
  setMultiSelectedText: (texts: TextData[]) => void;
  /** 기존 multiSelectedText와 병합. 같은 id가 있으면 해당 항목을 새 값으로 교체, 없으면 끝에 추가 */
  addMultiSelectedText: (texts: TextData[]) => void;
  cleanMultiSelectedText: () => void;

  hasChanged: boolean;
  /** 단일 활성 편집용 (스토어 impl과 동일) */
  imageChanged: boolean;
  isOverviewOpen: boolean;
  /** 보는 사람 선호. Body_03_2(기본) ↔ Body_02_2. 선택/클리어와 무관 */
  isLargeBody: boolean;
  toggleIsLargeBody: () => void;
  setIsLargeBody: (isLargeBody: boolean) => void;
  setActiveText: (activeText: TextData | null) => void;
  setOnlyAccessValue: (isPublic: boolean, tags?: TextTagData[]) => void;
  updateActiveText: (updates: Partial<TextData>) => void;
  syncTextTag: (tag: TextTagData) => void;
  removeTextTag: (tagId: number) => void;
  updateImage: (originUrl: string, width: number, height: number) => void;
  afterDeleteActiveText: () => void;
  setIsOverviewOpen: (isOpen: boolean) => void;

  activeTextBlockList: TextBlockData[];  
  setOnlyActiveTextBlockList: (activeTextBlockList: TextBlockData[]) => void;
  setActiveTextBlockList: (activeTextBlockList: TextBlockData[]) => void;
  /** `activeTextBlockList` 중 `type === IMAGE` 개수 */
  getActiveTextBlockImageCount: () => number;
  updateActiveTextBlockList: (id: string, updates: Partial<TextBlockData>) => void;
  afterDeleteActiveTextBlock: (id: string) => void;

  activeRelatedProjectList: ProjectRelatedData[];
  setActiveRelatedProjectList: (activeRelatedProjectList: ProjectRelatedData[]) => void;

  clearActiveText: () => void;

  multiHasChanged: boolean;
  setMultiHasChanged: (hasChanged: boolean) => void;
}

export const useTextViewStore = create<TextViewState>((set, get) => ({
  activeText: null,
  multiSelectedText: [],
  setMultiSelectedText: (texts) =>
    set((state) => {
      if (texts.length > 0) {
        /** 멀티만 활성화: 단일 상세(blocs) 제거해 TextViewer 블록과 충돌 방지 — Work 의 activeWork null + multi 패턴 대응 */
        return {
          multiSelectedText: texts,
          activeText: null,
          activeTextBlockList: [],
          hasChanged: false,
          imageChanged: false,
          multiHasChanged: false,
          isOverviewOpen: true,
        };
      }
      return {
        multiSelectedText: [],
        multiHasChanged: false,
        isOverviewOpen: !!state.activeText,
      };
    }),
  addMultiSelectedText: (incomingTexts) => {
    set((state) => {
      const nextMultiSelectedText = [...state.multiSelectedText];
      for (const text of incomingTexts) {
        const existingIndex = nextMultiSelectedText.findIndex((item) => item.id === text.id);
        if (existingIndex >= 0) {
          nextMultiSelectedText[existingIndex] = text;
        } else {
          nextMultiSelectedText.push(text);
        }
      }
      const hasMulti = nextMultiSelectedText.length > 0;
      return {
        multiSelectedText: nextMultiSelectedText,
        ...(hasMulti
          ? {
              activeText: null,
              activeTextBlockList: [],
              hasChanged: false,
              imageChanged: false,
              multiHasChanged: false,
            }
          : {}),
        isOverviewOpen: !!state.activeText || nextMultiSelectedText.length > 0,
      };
    });
  },
  cleanMultiSelectedText: () =>
    set((state) => ({
      multiSelectedText: [],
      isOverviewOpen: !!state.activeText,
    })),
  multiHasChanged: false,
  setMultiHasChanged: (multiHasChanged) => set({ multiHasChanged }),
  hasChanged: false,
  imageChanged: false,
  isOverviewOpen: false,
  isLargeBody: false,
  toggleIsLargeBody: () => set((state) => ({ isLargeBody: !state.isLargeBody })),
  setIsLargeBody: (isLargeBody) => set({ isLargeBody }),
  setActiveText: (activeText) =>
    set((state) => {
      const nextMulti = activeText ? [] : state.multiSelectedText;
      return {
        activeText,
        multiSelectedText: nextMulti,
        hasChanged: false,
        imageChanged: false,
        multiHasChanged: false,
        isOverviewOpen: !!activeText || nextMulti.length > 0,
      };
    }),
  setOnlyAccessValue: (isPublic, tags) =>
    set(({ activeText, hasChanged }) => ({
      activeText: activeText ?
        { ...activeText, isPublic, ...(tags !== undefined ? { tags } : {}) }
        : null,
      hasChanged
    })),
  updateActiveText: (updates) =>
    set(({ activeText }) => ({
      activeText: activeText ?
        { ...activeText, ...updates }
        : null,
      hasChanged: true
    })),
  syncTextTag: (tag) =>
    set(({ activeText, multiSelectedText }) => ({
      activeText: activeText
        ? {
            ...activeText,
            tags: (activeText.tags ?? []).map((textTag) =>
              textTag.id === tag.id ? tag : textTag
            ),
          }
        : null,
      multiSelectedText: multiSelectedText.map((text) => ({
        ...text,
        tags: (text.tags ?? []).map((textTag) =>
          textTag.id === tag.id ? tag : textTag
        ),
      })),
    })),
  removeTextTag: (tagId) =>
    set(({ activeText, multiSelectedText }) => ({
      activeText: activeText
        ? {
            ...activeText,
            tags: (activeText.tags ?? []).filter((tag) => tag.id !== tagId),
          }
        : null,
      multiSelectedText: multiSelectedText.map((text) => ({
        ...text,
        tags: (text.tags ?? []).filter((tag) => tag.id !== tagId),
      })),
    })),
  updateImage: (originUrl: string, width: number, height: number) =>
    set(({ activeText }) => ({
      activeText: activeText ? {
        ...activeText,
      } : null,
      hasChanged: true,
      imageChanged: true,
    })),
  afterDeleteActiveText: () =>
    set({
      activeText: null,
      multiSelectedText: [],
      hasChanged: false,
      imageChanged: false,
      multiHasChanged: false,
      activeTextBlockList: [],
      activeRelatedProjectList: [],
      isOverviewOpen: false,
    }),
  setIsOverviewOpen: (isOpen) => set({ isOverviewOpen: isOpen }),



  activeTextBlockList: [],
  getActiveTextBlockImageCount: () =>
    get().activeTextBlockList.filter((tb) => tb.type === TextBlockType.IMAGE).length,
  setOnlyActiveTextBlockList: (activeTextBlockList) => set({ activeTextBlockList }),
  setActiveTextBlockList: (activeTextBlockList) =>
    set(() => ({
      activeTextBlockList: activeTextBlockList.map((tb) => ({ ...tb, hasChanged: false, imageChanged: false })),
    })),

  updateActiveTextBlockList: (id, updates) =>
    set(({ activeTextBlockList }) => ({
      activeTextBlockList: activeTextBlockList.map((tb) =>
        tb.id === id ?
          { ...tb, ...updates, hasChanged: true } as TextBlockData
          : tb
      ),
    })),
  afterDeleteActiveTextBlock: (id) =>
    set(({ activeTextBlockList }) => ({
      activeTextBlockList: activeTextBlockList.filter((tb) => tb.id !== id)
    })),
  activeRelatedProjectList: [],
  setActiveRelatedProjectList: (activeRelatedProjectList) => set({ activeRelatedProjectList }),
  clearActiveText: () =>
    set({
      activeText: null,
      multiSelectedText: [],
      hasChanged: false,
      imageChanged: false,
      multiHasChanged: false,
      activeTextBlockList: [],
      activeRelatedProjectList: [],
      isOverviewOpen: false,
    }),
}));
